import React, { useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import AutocompleteDropdown from './AutocompleteDropdown';
import PatientModal from './PatientModal';
import PatientService from '../../services/patientService';
import { formatDateEs, calculateAge } from '../../utils';
import './PatientAutocomplete.css';

const patientAutocompleteFields = ['id', 'firstName', 'lastName', 'dateOfBirth', 'email', 'cellPhone'];

const PatientAutocomplete = ({
  selectedPatient,
  onPatientSelect,
  placeholder = "Seleccione un paciente",
  searchPlaceholder = "Buscar paciente...",
  disabled = false,
  className = '',
  showPatientId = true,
  showBirthDate = true,
  minSearchLength = 2,
  maxResults = 50,
  ...props
}) => {
  const auth = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Function to fetch patients based on search term
  const fetchPatients = useCallback(async (searchTerm, token) => {
    try {
      // If no search term or too short, return empty array
      if (!searchTerm || searchTerm.length < minSearchLength) {
        return [];
      }

      // Fetch patients from API
      const result = await PatientService.getAllPatientsAllPages(token, 500, patientAutocompleteFields);
      const patients = result.patients || [];
      
      // Client-side filtering
      const searchLower = searchTerm.toLowerCase().trim();
      const filteredPatients = patients.filter(patient => {
        const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
        
        return fullName.includes(searchLower) ||
               (patient.email && patient.email.toLowerCase().includes(searchLower)) ||
           (patient.cellPhone && patient.cellPhone.includes(searchTerm));
      });

      return filteredPatients.sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw new Error('Error al buscar pacientes');
    }
  }, [minSearchLength]);

  // Function to display patient text in the dropdown trigger
  const displayPatient = useCallback((patient) => {
    if (!patient) return '';
    return `${patient.firstName} ${patient.lastName}`;
  }, []);

  // Function to get unique key for each patient
  const getPatientKey = useCallback((patient) => {
    return patient.id;
  }, []);

  // Custom render function for patient items
  const renderPatientItem = useCallback((patient, isSelected, isHighlighted) => {
    const age = calculateAge(patient.dateOfBirth);

    return (
      <div className={`autocomplete-item ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}>
        <div className="patient-info">
          <div className="patient-name">
            {patient.firstName} {patient.lastName}
          </div>
          <div className="patient-details">
            {showBirthDate && patient.dateOfBirth && (
              <span className="patient-birth-date">
                {formatDateEs(patient.dateOfBirth)}
                {age !== null && ` (${age} años)`}
              </span>
            )}
            {patient.email && (
              <span className="patient-email">
                {patient.email}
              </span>
            )}
          </div>
        </div>
        {isSelected && (
          <i className="fas fa-check selected-icon"></i>
        )}
      </div>
    );
  }, [showBirthDate]);

  // Custom render function for selected patient preview
  const renderSelectedPreview = useCallback((patient) => {
    return (
      <div className="selected-patient-preview">
        <span className="patient-name">
          {patient.firstName} {patient.lastName}
        </span>
        {showBirthDate && patient.dateOfBirth && (
          <span className="patient-birth-date">
            ({formatDateEs(patient.dateOfBirth)})
          </span>
        )}
      </div>
    );
  }, [showBirthDate]);

  const renderAlwaysVisible = useCallback((searchTerm) => {
    return (
      <div className="add-patient-option">
        <button
          type="button"
          className="btn-add-patient"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Close the dropdown by simulating a click outside
            setTimeout(() => {
              document.body.click();
            }, 0);
            // Open modal after dropdown closes
            setTimeout(() => {
              setShowCreateModal(true);
            }, 100);
          }}
        >
          <i className="fas fa-plus"></i>
          <span>Agregar nuevo paciente</span>
        </button>
      </div>
    );
  }, []);

  // Handle patient creation
  const handlePatientCreated = useCallback((newPatient) => {
    // Automatically select the newly created patient
    onPatientSelect(newPatient);
    setShowCreateModal(false);
  }, [onPatientSelect]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <div className={showCreateModal ? 'hide-dropdown' : ''}>
        <AutocompleteDropdown
          fetchFunction={fetchPatients}
          displayFunction={displayPatient}
          keyFunction={getPatientKey}
          selectedItem={selectedPatient}
          onItemSelect={onPatientSelect}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          disabled={disabled}
          icon="fas fa-user"
          maxResults={maxResults}
          minSearchLength={minSearchLength}
          debounceDelay={300}
          showClearButton={true}
          emptyMessage="No se encontraron pacientes"
          loadingMessage="Buscando pacientes..."
          renderCustomItem={renderPatientItem}
          renderSelectedPreview={renderSelectedPreview}
          renderAlwaysVisible={renderAlwaysVisible}
          token={auth.user?.access_token}
          className={`patient-autocomplete ${className}`}
          searchOnFocus={false}
          clearOnSelect={false}
          {...props}
        />
      </div>
      
      <PatientModal
        isOpen={showCreateModal}
        onClose={handleModalClose}
        onPatientCreated={handlePatientCreated}
      />
    </>
  );
};

export default PatientAutocomplete;
